import React from 'react'
import Word from 'containers/RenderStroyComponent/Word'
import { useChineseLang } from '../../i18n/chinese'
import { CorrectionRun, CorrectionToken, CorrectionView, runIsVisible } from 'utils/correctionDiff'

interface Props {
  runs: CorrectionRun[]
  /** Which side of the correction to show: both, only yours, only the teacher's. */
  view: CorrectionView
  /** Namespaces the word ids so two corrections open at once cannot collide. */
  idPrefix: string
  activeIndex?: string
  onClickWord?: (event: any, word: any, sentence: any, key: string) => void
  fontSize?: string
}

/**
 * The teacher's markup, rendered as text you can still read and tap.
 *
 * Struck and added words go in `<del>` / `<ins>`, which is what they are: the
 * tint carries the meaning for sighted readers and the element carries it for
 * everyone else. Every token still renders through `Word`, so pinyin and
 * tap-to-look-up work here exactly as they do in the story.
 *
 * The tint is painted per word rather than around a whole run because the
 * passage is laid out with the reader's `word_wrapper` flex row: that is what
 * keeps the hanzi on a shared baseline whether or not a token carries pinyin,
 * and it is what lets a long correction wrap between any two words. Adjacent
 * words in the same run sit flush, so a run still reads as one block; only its
 * two ends are rounded.
 *
 * Deliberately NOT wired to the story's mastery colours: the words already
 * carry a green or a red ground here, and a second set of coloured underlines
 * on top is what made the old page unreadable.
 */
/** Rounds the two ends of a marked run: its first and last token. */
const edgeClasses = (index: number, length: number) =>
  `${index === 0 ? ' is-first' : ''}${index === length - 1 ? ' is-last' : ''}`

const CorrectionText: React.FC<Props> = ({ runs, view, idPrefix, activeIndex, onClickWord, fontSize }) => {
  const zhLang = useChineseLang()
  const visible = runs.filter((run) => runIsVisible(run.kind, view))
  // The whole passage is the lookup context, so a tapped word is translated in
  // its own sentence rather than in whatever correction happens to be first.
  const sentence: CorrectionToken[] = visible.flatMap((run) => run.tokens)

  return (
    <div className={`cx-text word_wrapper cx-text--${view}`} lang={zhLang} translate='no'>
      {visible.map((run, runIndex) =>
        run.tokens.map((token, tokenIndex) => {
          const key = `${idPrefix}-${runIndex}-${tokenIndex}`
          const word = (
            <Word
              index={key}
              isActive={key === activeIndex}
              text={token.word}
              textObj={token}
              sentence={sentence}
              onClick={onClickWord}
              pinYinText={token.pinyin || null}
              pinyinFontSize='11px'
              chineseWordFontSize={fontSize || '21px'}
            />
          )

          if (run.kind === 'same') {
            return (
              <span className='cx-tok' key={key}>
                {word}
              </span>
            )
          }

          const edges = edgeClasses(tokenIndex, run.tokens.length)
          const className = `cx-tok cx-tok--${run.kind}${edges}`

          return run.kind === 'added' ? (
            <ins className={className} key={key}>
              {word}
            </ins>
          ) : (
            <del className={className} key={key}>
              {word}
            </del>
          )
        }),
      )}
    </div>
  )
}

export default CorrectionText
